/** @jsx jsx */

import { ComponentProps, useEffect, useState } from 'react';

import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { getUrls } from '@zapier/product-dev-platform/src/utils/urls';
import { css, jsx } from '@emotion/core';

import AuthButtons from './AuthButtons';
import LogoZapierPlatform from '../LogoZapierPlatform';
import NavLinks from './NavLinks';
import SkipLink from './SkipLink';
import UserDropdown from './UserDropdown';
import { MEDIA_MIN_DESKTOP } from './constants';
import { getNavLinks } from './utils';

const Styles = {
  container: css`
    align-items: center;
    background-color: inherit;
    color: inherit;
    display: flex;
    height: 100%;
    justify-content: space-between;
    margin: auto;
    position: relative;
    z-index: 1;
}
  `,
  logo: css`
    display: flex;
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
    ${[MEDIA_MIN_DESKTOP]} {
      position: static;
      transform: none;
    },
  `,
  menuButton: (isLightTheme: boolean) => css`
    background-color: transparent;
    border: none;
    color: ${isLightTheme ? 'inherit' : Colors.neutral600};
    cursor: pointer;
    ${[MEDIA_MIN_DESKTOP]} {
      display: none;
    }
  `,
  rightSplit: (isNavOpen: boolean) => css`
    background-color: inherit;
    display: ${isNavOpen ? 'flex' : 'none'};
    flex-direction: column;
    left: -30px;
    position: absolute;
    right: -30px;
    top: 100%;
    ${[MEDIA_MIN_DESKTOP]} {
      align-items: center;
      align-self: stretch;
      display: flex;
      flex-direction: row;
      flex: 1 0 auto;
      left: auto;
      margin-left: 40px;
      position: relative;
      right: auto;
      top: auto;
    },

  `,
  root: (isLightTheme: boolean) => css`
    ${Typography.base};
    color: ${isLightTheme ? Colors.neutral800 : Colors.neutral100};
    background-color: ${isLightTheme ? Colors.neutral100 : Colors.neutral800};
    height: 68px;
    padding: 0 30px;
  `,
};

type NavLink = Exclude<ComponentProps<typeof NavLinks>['links'], undefined>[0];

type Props = {
  /** The curent URL of the page. */
  currentUrl: string;
  /** Links to be used in the left navigation section. */
  navLinks?: NavLink[];
  /** Callback when clicking to logout. */
  onClickLogIn?: () => void;
  /** Callback when clicking to logout. */
  onClickLogOut?: () => void;
  /** Callback when clicking to logout. */
  onClickSignUp?: () => void;
  /** Callback when the user clicks on the menu icon button. */
  onClickMenuButton?: () => void;
  /** Callback when the user clicks on a navigation link. */
  onClickNavLink?: ({ href, label }: NavLink) => void;
  /** Callback when clicking to go to zapier.com. */
  onClickZapier?: () => void;
  /** Callback when clicking to go to Zap Templates. */
  onClickZapTemplates?: () => void;
  /** Callback when clicking on the Zapier Platform logo. */
  onClickZapierPlatformLogo?: () => void;
  /** Callback when clicking to open settings. */
  onClickSettings?: () => void;
  /** Callback when toggling the user dropdown. */
  onToggleUserDropdown?: (isOpen: boolean) => void;
  /** Theme of the header. */
  theme: 'light' | 'dark';
  /** Anchor to the main content area of the page. The page must contain a node with the id in this prop.  */
  urlSkipLink?: string;
  /** The current user's avatar URL and name. */
  user?: {
    avatarUrl: string;
    name: string;
  };
};

const defaultProps = {
  navLinks: undefined,
  onClickLogIn: () => undefined,
  onClickLogOut: () => undefined,
  onClickSignUp: () => undefined,
  onClickMenuButton: () => undefined,
  onClickNavLink: () => undefined,
  onClickSettings: () => undefined,
  onClickZapTemplates: () => undefined,
  onClickZapier: () => undefined,
  onClickZapierPlatformLogo: () => undefined,
  onToggleUserDropdown: () => undefined,
  theme: 'dark' as const,
  urlSkipLink: undefined,
  user: undefined,
};

// This component is isomorphic since it's consumed by web-ssr at the moment.
function Header({
  currentUrl,
  navLinks = defaultProps.navLinks,
  onClickLogIn = defaultProps.onClickLogIn,
  onClickLogOut = defaultProps.onClickLogOut,
  onClickSignUp = defaultProps.onClickSignUp,
  onClickMenuButton = defaultProps.onClickMenuButton,
  onClickNavLink = defaultProps.onClickNavLink,
  onClickSettings = defaultProps.onClickSettings,
  onClickZapier = defaultProps.onClickZapier,
  onClickZapTemplates = defaultProps.onClickZapTemplates,
  onClickZapierPlatformLogo = defaultProps.onClickZapierPlatformLogo,
  onToggleUserDropdown = defaultProps.onToggleUserDropdown,
  theme = defaultProps.theme,
  urlSkipLink = defaultProps.urlSkipLink,
  user = defaultProps.user,
}: Props) {
  const isLightTheme = theme === 'light';

  const [host, setHost] = useState('');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  useEffect(() => {
    // window object is available since this runs only in the browser.
    setHost(window.location.host);
  }, []);

  const urls = getUrls(host);

  return (
    <header css={Styles.root(isLightTheme)}>
      <div css={Styles.container}>
        <button
          css={Styles.menuButton(isLightTheme)}
          onClick={() => {
            onClickMenuButton();
            setIsNavOpen(!isNavOpen);
          }}
        >
          <Icon name={isNavOpen ? 'x' : 'navMenu'} />
        </button>
        <Link
          aria-label="Zapier Developer Platform"
          css={Styles.logo}
          href={urls.developerPlatform}
          onClick={onClickZapierPlatformLogo}
          tabIndex={0}
        >
          <LogoZapierPlatform
            theme={isLightTheme ? 'light' : 'dark'}
            width={192}
          />
        </Link>
        <SkipLink href={urlSkipLink} text="Skip to main content" />
        <div css={Styles.rightSplit(isNavOpen)}>
          <NavLinks
            currentUrl={currentUrl}
            isAuthed={!!user}
            isLightTheme={isLightTheme}
            links={navLinks || getNavLinks(!!user, host)}
            onClickLink={onClickNavLink}
            urlSignUp={urls.signUp}
          />
        </div>
        {user ? (
          <UserDropdown
            isOpen={isUserDropdownOpen}
            onClickLogOut={onClickLogOut}
            onClickSettings={() => {
              onClickSettings();
              setIsUserDropdownOpen(false);
            }}
            onClickZapier={() => {
              onClickZapier();
              setIsUserDropdownOpen(false);
            }}
            onClickZapTemplates={() => {
              onClickZapTemplates();
              setIsUserDropdownOpen(false);
            }}
            onToggle={() => {
              onToggleUserDropdown(!isUserDropdownOpen);
              setIsUserDropdownOpen(!isUserDropdownOpen);
            }}
            urlLogOut={urls.logOut}
            urlSettings={urls.settings}
            urlZapier={urls.zapier}
            urlZapTemplates={urls.zapTemplates}
            user={user}
          />
        ) : (
          <AuthButtons
            onClickLogIn={onClickLogIn}
            onClickSignUp={onClickSignUp}
            urlLogIn={urls.logIn}
            urlSignUp={urls.signUp}
          />
        )}
      </div>
    </header>
  );
}

Header.defaultProps = defaultProps;

export default Header;
